import {
  Button, Space, Tooltip, Tag, message, Breadcrumb, Input, Select,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, SearchOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { doctorApi } from '../../../api/doctor.api';
import { clinicApi } from '../../../api/clinic.api';
import DataTable from '../../../components/common/DataTable';
import DoctorForm from '../components/DoctorForm';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import useDebounce from '../../../hooks/useDebounce';
import { getApiErrorMessage } from '../../../utils/helpers';

// ─── Main Component ────────────────────────────────────────────────
const DoctorList = () => {
  const [formOpen,     setFormOpen]     = useState(false);
  const [editRecord,   setEditRecord]   = useState(null);
  const [searchInput,  setSearchInput]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clinicFilter, setClinicFilter] = useState('');
  const [doctorTypeFilter, setDoctorTypeFilter] = useState('');
  const [pagination,   setPagination]   = useState({ page: 1, limit: 10 });
  const [sort,         setSort]         = useState({ sortBy: 'createdAt', sortOrder: 'desc' });

  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  const debouncedSearch = useDebounce(searchInput, 300);

  // ── Data fetch ─────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['doctors', debouncedSearch, statusFilter, clinicFilter, doctorTypeFilter, sort, pagination],
    queryFn:  () => doctorApi.getAll({
      search: debouncedSearch,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(clinicFilter ? { clinic: clinicFilter } : {}),
      ...(doctorTypeFilter ? { doctorType: doctorTypeFilter } : {}),
      ...sort,
      ...pagination,
    }),
  });

  const { data: clinicsData } = useQuery({
    queryKey: ['clinics-active'],
    queryFn:  () => clinicApi.getActive(),
  });

  // ── Mutations ──────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: doctorApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['doctors']);
      message.success('Doctor added with login access');
      setFormOpen(false);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => doctorApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['doctors']);
      message.success('Doctor updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: doctorApi.delete,
    onSuccess: () => { queryClient.invalidateQueries(['doctors']); message.success('Doctor deleted'); },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const handleDeleteClick = async (record) => {
    let referralCount = 0;
    try {
      const res = await doctorApi.getReferralCount(record.id);
      referralCount = res?.data?.count || 0;
    } catch (err) {
      message.error(getApiErrorMessage(err));
      return;
    }

    showDeleteConfirm({
      content: referralCount > 0
        ? `Are you sure you want to delete this doctor? ${referralCount} referral child${referralCount > 1 ? 'ren are' : ' is'} currently assigned to them and will become unassigned.`
        : 'Are you sure you want to delete this doctor?',
      onOk: () => deleteMutation.mutate(record.id),
    });
  };

  const handleFormSubmit = (values) => {
    const { confirmPassword, ...payload } = values;
    if (!payload.password) { delete payload.password; }
    if (editRecord) { updateMutation.mutate({ id: editRecord.id, data: payload }); }
    else            { createMutation.mutate(payload); }
  };

  // ── Search ─────────────────────────────────────────────────────
  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  // ── Table change: handles both sort and pagination ──────────────
  const handleTableChange = useCallback((newPagination, _filters, sorter) => {
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setSort(nextSorter?.order
      ? {
          sortBy:    nextSorter.field || nextSorter.columnKey,
          sortOrder: nextSorter.order === 'ascend' ? 'asc' : 'desc',
        }
      : { sortBy: 'createdAt', sortOrder: 'desc' });
    setPagination({ page: newPagination.current || 1, limit: newPagination.pageSize || 10 });
  }, []);

  // ── Derived data ───────────────────────────────────────────────
  const allDoctors = data?.data || [];
  const meta       = data?.meta || {};

  const getSortOrder = (field) => (
    sort.sortBy === field ? (sort.sortOrder === 'asc' ? 'ascend' : 'descend') : null
  );

  // ── Columns ────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Doctor Name', dataIndex: 'name', key: 'name', ellipsis: true,
      sorter: true, sortOrder: getSortOrder('name'),
    },
    {
      title: 'Mobile No.', dataIndex: 'phone', key: 'phone', width: 140,
      sorter: true, sortOrder: getSortOrder('phone'),
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true,
      sorter: true, sortOrder: getSortOrder('email'),
    },
    {
      title: 'Specialization', dataIndex: 'specialization', key: 'specialization', ellipsis: true,
      sorter: true, sortOrder: getSortOrder('specialization'),
    },
    {
      title: 'Clinic', key: 'clinic', dataIndex: 'clinic', ellipsis: true,
      render: (clinic) => clinic?.name || '-',
    },
    {
      title: 'Doctor Type', key: 'isReferral', dataIndex: 'isReferral', width: 140,
      render: (v) => (
        <Tag color={v ? 'blue' : 'purple'} style={{ borderRadius: 9999, fontSize: 11 }}>
          {v ? 'Referral Doctor' : 'Event Doctor'}
        </Tag>
      ),
    },
    {
      title: 'Account Status', key: 'isActive', dataIndex: 'isActive', width: 140,
      sorter: true, sortOrder: getSortOrder('isActive'),
      render: (v) => (
        <Tag color={v ? 'green' : 'red'} style={{ borderRadius: 9999, fontSize: 11 }}>
          {v ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 90,
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined />}
              style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
              onClick={() => { setEditRecord(record); setFormOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              shape="circle"
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
              onClick={() => handleDeleteClick(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Doctors' },
        ]}
      />

      {/* Toolbar */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        marginBottom: 16,
        background:   '#fff',
        borderRadius: 14,
        padding:      '10px 16px',
        boxShadow:    '0 2px 8px rgba(var(--wl-primary-rgb),0.08)',
        border:       '1px solid var(--wl-primary-pale)',
      }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by name, mobile, email, specialization…"
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{ flex: 1, maxWidth: 340, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
        /> 
        <Select
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val); setPagination((p) => ({ ...p, page: 1 })); }}
          allowClear={!!statusFilter}
          onClear={() => { setStatusFilter(''); setPagination((p) => ({ ...p, page: 1 })); }}
          clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
          style={{ width: 130 }}
          options={[
            { label: 'All',      value: '' },
            { label: 'Active',   value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
        />
        <Select
          value={clinicFilter}
          onChange={(val) => { setClinicFilter(val); setPagination((p) => ({ ...p, page: 1 })); }}
          allowClear={!!clinicFilter}
          onClear={() => { setClinicFilter(''); setPagination((p) => ({ ...p, page: 1 })); }}
          clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
          placeholder="All Clinics"
          showSearch
          optionFilterProp="label"
          style={{ width: 180 }}
          options={[
            { label: 'All Clinics', value: '' },
            ...(clinicsData?.data || []).map((c) => ({ label: c.name, value: c.id })),
          ]}
        />
        <Select
          value={doctorTypeFilter}
          onChange={(val) => { setDoctorTypeFilter(val); setPagination((p) => ({ ...p, page: 1 })); }}
          allowClear={!!doctorTypeFilter}
          onClear={() => { setDoctorTypeFilter(''); setPagination((p) => ({ ...p, page: 1 })); }}
          clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
          style={{ width: 160 }}
          options={[
            { label: 'All Doctor Types', value: '' },
            { label: 'Referral Doctor',  value: 'referral' },
            { label: 'Event Doctor',     value: 'event' },
          ]}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => { setEditRecord(null); setFormOpen(true); }}
        >
          Add Doctor
        </Button>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        dataSource={allDoctors}
        loading={isLoading}
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 900 }}
        pagination={{
          current:  pagination.page,
          pageSize: pagination.limit,
          total:    meta.total,
        }}
        onChange={handleTableChange}
      />

      {/* Add / Edit form */}
      <DoctorForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
        loading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
};

export default DoctorList;
